import React from "react";
import { Link } from "react-router-dom";
import CinematicJourney from "../components/CinematicJourney";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { MEDIA } from "../data/media";
import { Check, X, ArrowUpRight } from "lucide-react";

const TOOLS = ["Excel", "Monday", "QuickBooks", "Dropbox", "SharePoint", "HubSpot", "Zapier", "Google Drive", "ClickUp", "Calendly", "ChatGPT", "Claude"];
const IMAGINE = ["Your documents", "Your CRM", "Your customer portal", "Your AI assistant", "Your dashboards", "Your workflows", "Your reporting"];
const BEFORE = ["Paying $8,000–$15,000 a year in software", "Living in spreadsheets", "Double-entering data", "Hunting for information", "Switching between ten platforms"];
const AFTER = ["One login", "One platform", "One source of truth", "Built around your business", "Software you own — not rent"];
const STEPS = [
  { k: "01", t: "Discover", d: "I learn how your business actually operates — the tools, the workflows, the friction. No assumptions, no templates." },
  { k: "02", t: "Design", d: "I map it into one platform: connected data, clear flows, built around the way you work." },
  { k: "03", t: "Build", d: "I build it — with automation and AI applied exactly where it counts." },
  { k: "04", t: "Evolve", d: "The platform never stops improving. Because technology is only valuable when it delivers results." },
];
const TRANSFORM = [
  "Your staff stop chasing information.",
  "Management sees the business in real time.",
  "Customers get answers instantly.",
  "Manual work disappears.",
  "Software subscriptions shrink.",
  "Your business finally works the way you've always wanted.",
];
const PROOF = [
  { t: "Engineering", d: "Replaced 14 spreadsheets with one operational platform." },
  { t: "Training", d: "Built a complete learning and certification system." },
  { t: "Consultancy", d: "Centralised knowledge, documents and client reporting." },
  { t: "Construction", d: "Live dashboards replacing manual reporting." },
];

export default function Home() {
  return (
    <div data-testid="page-home">
      <Seo
        title="AberCal — Custom Business Platforms & AI Studio | Calgary"
        description="Most businesses adapt to software. AberCal engineers custom business platforms that adapt to your business — plus websites, AI images, animation, video and AI video ads. Calgary, Alberta."
        path="/"
      />
      <CinematicJourney />

      {/* Emotional hook */}
      <section data-testid="hook-section" className="relative border-t border-[var(--border)] bg-[var(--bg)] px-6 py-32">
        <div className="mx-auto max-w-4xl">
          <Reveal>
            <p className="font-head text-3xl font-light leading-snug tracking-tight sm:text-4xl lg:text-5xl">
              Your business wasn't built to manage software.
            </p>
          </Reveal>
          <Reveal delay={0.08}>
            <p className="mt-6 font-head text-2xl font-light leading-snug text-[var(--text-2)] sm:text-3xl">
              It was built to serve customers. Grow. Innovate. Make money.
            </p>
          </Reveal>
          <div className="mt-14 space-y-3">
            {["One spreadsheet became ten.", "One subscription became fifteen.", "One login became thirty."].map((t, i) => (
              <Reveal key={t} delay={0.1 + i * 0.08}>
                <p className="font-head text-xl font-light text-[var(--text)] sm:text-2xl">{t}</p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.3}>
            <p className="mt-14 max-w-2xl font-head text-2xl font-light leading-snug text-[var(--text-2)] sm:text-3xl">
              Your business slowly started adapting to software — instead of software adapting to your business.
            </p>
          </Reveal>
          <Reveal delay={0.4}>
            <p className="mt-8 font-head text-3xl font-light tracking-tight text-gold sm:text-4xl">That's what AberCal changes.</p>
          </Reveal>
        </div>
      </section>

      {/* The problem */}
      <section data-testid="problem-section" className="relative border-t border-[var(--border)] bg-[var(--bg)] px-6 py-32">
        <div className="mx-auto max-w-5xl">
          <Reveal><p className="mb-5 text-xs uppercase tracking-[0.3em] text-gold">The problem</p></Reveal>
          <Reveal delay={0.05}>
            <h2 className="max-w-3xl font-head text-4xl font-light leading-tight tracking-tight sm:text-5xl">
              You're probably running your business on a dozen disconnected tools.
            </h2>
          </Reveal>
          <div className="mt-12 flex flex-wrap gap-3">
            {TOOLS.map((t, i) => (
              <Reveal key={t} delay={i * 0.06}>
                <span className="border border-[var(--border)] px-4 py-2 text-sm text-[var(--muted)] line-through decoration-[var(--red)]/60">{t}</span>
              </Reveal>
            ))}
            <Reveal delay={TOOLS.length * 0.06}><span className="px-4 py-2 text-sm italic text-[var(--text-2)]">…and twenty other subscriptions.</span></Reveal>
          </div>
          <Reveal delay={0.15}>
            <p className="mt-16 font-head text-4xl font-light tracking-tight sm:text-5xl lg:text-6xl">
              What if they all became <span className="text-gold italic">one platform?</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* Imagine if */}
      <section className="relative overflow-hidden px-6 py-32">
        <img src={MEDIA.weave} alt="" aria-hidden className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-10" />
        <div className="pointer-events-none absolute inset-0 bg-[var(--bg)]/80" />
        <div className="relative mx-auto max-w-5xl">
          <Reveal><p className="mb-8 text-xs uppercase tracking-[0.3em] text-gold">Imagine if</p></Reveal>
          <div className="grid grid-cols-1 gap-x-12 gap-y-4 sm:grid-cols-2">
            {IMAGINE.map((it, i) => (
              <Reveal key={it} delay={i * 0.06}>
                <div className="flex items-center gap-4 border-b border-[var(--border)] py-4">
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-gold" />
                  <span className="font-head text-2xl font-light text-[var(--text)]">{it}</span>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-14 max-w-3xl font-head text-3xl font-light leading-snug tracking-tight sm:text-4xl">
              …all lived in one place. <span className="text-gold italic">Built specifically for your business.</span><br />
              <span className="text-[var(--text-2)]">Not someone else's.</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* Statement — full screen, let it breathe */}
      <section data-testid="statement-section" className="flex min-h-screen items-center justify-center border-y border-[var(--border)] bg-[var(--surface)] px-6 text-center">
        <Reveal>
          <p className="font-head text-5xl font-light leading-[1.05] tracking-tight sm:text-7xl lg:text-8xl">I don't build software.</p>
          <p className="mt-4 font-head text-5xl font-light leading-[1.05] tracking-tight text-gold sm:text-7xl lg:text-8xl">I build competitive advantages.</p>
        </Reveal>
      </section>

      {/* The shift — chaos vs calm */}
      <section data-testid="shift-section" className="px-6 py-32">
        <div className="mx-auto max-w-6xl">
          <Reveal><p className="mb-4 text-xs uppercase tracking-[0.3em] text-gold">The shift</p></Reveal>
          <Reveal delay={0.05}><h2 className="mb-16 font-head text-4xl font-light tracking-tight sm:text-5xl">One platform instead of ten.</h2></Reveal>
          <div className="grid grid-cols-1 gap-px overflow-hidden border border-[var(--border)] md:grid-cols-2">
            <Reveal>
              <div className="h-full bg-[var(--bg)] p-10">
                <p className="mb-2 font-head text-3xl font-light text-[var(--red)]">Chaos</p>
                <p className="mb-8 text-xs uppercase tracking-[0.3em] text-[var(--muted)]">Typical business</p>
                <ul className="space-y-5">
                  {BEFORE.map((b) => (
                    <li key={b} className="flex items-start gap-3 text-[var(--text-2)]">
                      <X size={18} className="mt-0.5 shrink-0 text-[var(--red)]" /> <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="h-full bg-[var(--elevated)] p-10">
                <p className="mb-2 font-head text-3xl font-light text-gold">Calm</p>
                <p className="mb-8 text-xs uppercase tracking-[0.3em] text-gold">After AberCal</p>
                <ul className="space-y-5">
                  {AFTER.map((a) => (
                    <li key={a} className="flex items-start gap-3 text-[var(--text)]">
                      <Check size={18} className="mt-0.5 shrink-0 text-gold" /> <span>{a}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Own vs rent */}
      <section className="relative border-t border-[var(--border)] px-6 py-36 text-center">
        <Reveal>
          <p className="mb-6 font-head text-2xl italic text-[var(--text-2)]">You're already paying.</p>
          <h2 className="mx-auto max-w-4xl font-head text-4xl font-light leading-tight tracking-tight sm:text-6xl">
            Do you want to keep renting software — <br className="hidden sm:block" /><span className="text-gold">or own the platform your business runs on?</span>
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="tartan-line mx-auto my-16 w-24" />
          <p className="mx-auto max-w-3xl font-head text-3xl font-light italic tracking-tight text-[var(--text)] sm:text-4xl">
            "I'm not replacing people. I'm replacing software."
          </p>
        </Reveal>
      </section>

      {/* What changes after AberCal */}
      <section data-testid="transform-section" className="relative overflow-hidden border-t border-[var(--border)] bg-[var(--surface)] px-6 py-32">
        <div className="relative mx-auto max-w-5xl">
          <Reveal><p className="mb-8 text-xs uppercase tracking-[0.3em] text-gold">What changes after AberCal</p></Reveal>
          <div className="space-y-4">
            {TRANSFORM.map((t, i) => (
              <Reveal key={t} delay={i * 0.08}>
                <p className="flex items-start gap-4 font-head text-2xl font-light text-[var(--text)] sm:text-3xl">
                  <Check size={22} className="mt-1.5 shrink-0 text-gold" /> {t}
                </p>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-16 font-head text-3xl font-light tracking-tight sm:text-4xl">
              You're not buying software. <span className="text-gold italic">You're investing in the way your business will operate for years to come.</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* Proof — built to solve real problems */}
      <section data-testid="proof-section" className="border-t border-[var(--border)] bg-[var(--bg)] px-6 py-32">
        <div className="mx-auto max-w-7xl">
          <Reveal><p className="mb-4 text-xs uppercase tracking-[0.3em] text-gold">Built to solve real problems</p></Reveal>
          <Reveal delay={0.05}><h2 className="max-w-3xl font-head text-4xl font-light tracking-tight sm:text-5xl">Not companies. <span className="text-gold italic">Problems.</span></h2></Reveal>
          <div className="mt-16 grid grid-cols-1 gap-px overflow-hidden border border-[var(--border)] sm:grid-cols-2">
            {PROOF.map((p, i) => (
              <Reveal key={p.t} delay={(i % 2) * 0.1}>
                <div data-testid={`proof-${i}`} className="group h-full bg-[var(--surface)] p-10 transition-colors duration-500 hover:bg-[var(--elevated)]">
                  <p className="text-xs uppercase tracking-[0.3em] text-gold">{p.t}</p>
                  <p className="mt-5 font-head text-2xl font-light leading-snug text-[var(--text)] sm:text-3xl">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="border-t border-[var(--border)] bg-[var(--bg)] px-6 py-32">
        <div className="mx-auto max-w-7xl">
          <Reveal><p className="mb-4 text-xs uppercase tracking-[0.3em] text-gold">How it works</p></Reveal>
          <Reveal delay={0.05}><h2 className="max-w-3xl font-head text-4xl font-light tracking-tight sm:text-5xl">Four steps. One platform that never stops improving.</h2></Reveal>
          <div className="mt-16 grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <Reveal key={s.k} delay={i * 0.1}>
                <div>
                  <div className="flex items-center gap-4">
                    <span className="font-head text-5xl font-light text-gold">{s.k}</span>
                    {i < STEPS.length - 1 && <span className="hidden h-px flex-1 bg-[var(--border)] lg:block" />}
                  </div>
                  <h3 className="mt-6 font-head text-3xl font-light">{s.t}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-[var(--text-2)]">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <Link to="/services" className="btn-ghost mt-16 inline-flex items-center gap-2">See everything I build <ArrowUpRight size={14} /></Link>
          </Reveal>
        </div>
      </section>

      {/* The question every platform starts with */}
      <section data-testid="question-section" className="border-t border-[var(--border)] px-6 py-36 text-center">
        <div className="mx-auto max-w-4xl">
          <Reveal><p className="mb-8 text-xs uppercase tracking-[0.3em] text-gold">Every platform starts with one question</p></Reveal>
          <Reveal delay={0.05}>
            <p className="font-head text-4xl font-light leading-tight tracking-tight sm:text-6xl">"What are you trying to <span className="text-gold italic">achieve?</span>"</p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-8 font-head text-2xl font-light text-[var(--text-2)] sm:text-3xl">Not "What software do you need?"</p>
          </Reveal>
        </div>
      </section>

      {/* Positioning + Final CTA */}
      <section className="relative overflow-hidden border-t border-[var(--border)] px-6 py-40 text-center">
        <img src={MEDIA.forge} alt="" aria-hidden className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-15" />
        <div className="pointer-events-none absolute inset-0 bg-[var(--bg)]/75" />
        <div className="relative">
          <Reveal>
            <p className="mb-8 text-xs uppercase tracking-[0.3em] text-gold">AberCal</p>
            <h2 className="mx-auto max-w-4xl font-head text-4xl font-light leading-tight tracking-tight sm:text-6xl">
              The platform your business <span className="text-gold italic">wishes already existed.</span>
            </h2>
            <p className="mx-auto mt-8 font-head text-xl italic text-[var(--text-2)]">If you can dream it, I can build it.</p>
            <Link to="/contact" data-testid="home-final-cta" className="btn-gold mt-12 inline-block">Start a Conversation</Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
